import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { salaryGradesApi } from '../plantillaApi';
import { salaryTypeLabel, type SalaryGradeListItem, type SalaryTypeValue } from '../plantillaTypes';
import { SALARY_STEPS } from '../utils/sampleSalaryGrades';

const AMOUNT_ID = 'salary-grade-rate-monthly-amount';

/**
 * Correct ONE rate — the step cell the reader clicked on the tranche's grid (product call,
 * 19 Oct 2026: "once a cell is clicked display a modal of the SG and step amount, editable").
 *
 * It saves through the grade's LINE (`PUT …/grades/{grade}`), sending the line's other rates
 * as they are, so the ordering rule — along the steps and down the grades — is checked by the
 * one server path that already holds it. A refusal names `stepRates.N`; every one of them is
 * this form's single amount field.
 *
 * **No Autogenerate**, deliberately — the form is FILLED AT MOUNT with the rate it corrects
 * (the `<ResetUserPasswordModal>` exemption).
 */
export function SalaryGradeRateModal({
  open,
  type,
  tranche,
  grade,
  step,
  rates,
  onClose,
  onSaved,
}: {
  open: boolean;
  type: SalaryTypeValue;
  tranche: number;
  grade: number;
  /** By position: 0 is step 1. */
  step: number;
  /** The grade's whole line, by position — the other rates go back unchanged. */
  rates: (SalaryGradeListItem | null)[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [value, setValue] = useState(() => String(rates[step]?.monthlyAmount ?? ''));
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const toast = useToast();

  const amount = Number(value);
  const valid = value.trim() !== '' && Number.isFinite(amount) && amount >= 0 && amount <= 10_000_000;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await salaryGradesApi.updateGradeLine(type, tranche, grade, {
        stepRates: rates.map((rate, position) => (position === step ? amount : (rate?.monthlyAmount ?? null))),
      });

      toast.success(`SG ${grade} step ${step + 1} updated.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the rate.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'salary-grade-rate',
        // Every step the server could name is this one field.
        fieldIds: Object.fromEntries(
          Array.from({ length: SALARY_STEPS }, (_, position) => [`stepRates.${position}`, AMOUNT_ID]),
        ),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit SG ${grade} step ${step + 1}`}
      size="sm"
      id="salary-grade-rate-modal"
      confirm={{
        id: 'salary-grade-rate-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="salary-grade-rate-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="salary-grade-rate-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* Which rate this is — read-only facts. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Type', value: salaryTypeLabel(type) },
            { label: 'Tranche', value: String(tranche), mono: true },
            { label: 'Grade', value: `SG ${grade}` },
            { label: 'Step', value: String(step + 1), mono: true },
          ]}
        />

        <TextField
          id={AMOUNT_ID}
          label="Monthly rate"
          required
          type="number"
          min={0}
          step="0.01"
          description="Must stay higher than the step before it and the grade above it at this step."
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={value === '' || valid ? undefined : 'Enter an amount up to 10,000,000.'}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
